import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http'
import {
  APP_INITIALIZER,
  ApplicationConfig,
  EnvironmentProviders,
  Provider,
} from '@angular/core'
import { bootstrapApplication } from '@angular/platform-browser'
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async'
import { provideRouter, RouteReuseStrategy, Routes } from '@angular/router'
import { definePreset } from '@primeng/themes'
import Aura from '@primeng/themes/aura'
import { providePrimeNG } from 'primeng/config'
import './polyfills-node'

import { MainComponent } from './app/components/main/main.component'
import { ReportsComponent } from './app/components/reports/reports.component'
import { SyncAuditComponent } from './app/components/sync-audit/sync-audit.component'
import { SyncQuarantineComponent } from './app/components/sync-quarantine/sync-quarantine.component'
import { PendingSyncsComponent } from './app/components/pending-syncs/pending-syncs.component'
import { RepaymentExportsComponent } from './app/components/repayment-exports/repayment-exports.component'
import { TabRouteReuseStrategy } from './app/route-reuse.strategy'
import { APP_CONFIG } from './config/environment'

import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular'
import {
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication,
} from '@azure/msal-browser'
import { RecentUploadsComponent } from './app/components/recent-uploads/recent-uploads.component'
import { RepaymentsTableComponent } from './app/components/repayments-table/repayments-table.component'
import { RepaymentsViewComponent } from './app/components/repayments-view/repayments-view.component'
import { WrapperComponent } from './app/components/wrapper/wrapper.component'
import { AuthService } from './service/auth.service'
import { FINANCE365_THEME_EXTENSION } from './app/theme/finance365-theme'

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0', '::1'])

function isLocalHost(hostname: string): boolean {
  const normalized = hostname.toLowerCase()
  return (
    LOCAL_HOSTS.has(normalized) ||
    normalized.endsWith('.localhost') ||
    normalized.endsWith('.local')
  )
}

const isLocal = isLocalHost(location.hostname)
const hasCanvasCode = new URLSearchParams(location.search).has('canvasCode')
const isCypress =
  typeof (window as unknown as { Cypress?: unknown }).Cypress !== 'undefined'
const isEmbeddedPath = /^\/embed-(?:view|legal|operations|merchant|all)$/.test(
  location.pathname,
)
const isEmbedded =
  isEmbeddedPath ||
  hasCanvasCode ||
  (isLocal && isCypress)
const useMsal = !isEmbedded

function buildRoutes(): Routes {
  const rootRoute: Routes[number] = {
    path: '',
    component: MainComponent,
    children: [
      { path: '', redirectTo: 'repayments', pathMatch: 'full' as const },
      { path: 'repayments', component: RepaymentsViewComponent },
      { path: 'in-progress', component: RepaymentsViewComponent },
      { path: 'merchants', component: RepaymentsViewComponent },
      { path: 'terminals', component: RepaymentsViewComponent },
      { path: 'cash-advances', component: RepaymentsViewComponent },
      { path: 'reports', component: ReportsComponent },
      { path: 'sync-audit', component: SyncAuditComponent },
      { path: 'sync-quarantine', component: SyncQuarantineComponent },
      { path: 'pending-syncs', component: PendingSyncsComponent },
      { path: 'repayment-exports', component: RepaymentExportsComponent },
      { path: 'uploaded-files', component: RecentUploadsComponent },
    ],
  }

  if (useMsal) {
    rootRoute.canActivate = [MsalGuard]
  }

  return [
    rootRoute,
    {
      path: 'embed-view',
      component: RepaymentsTableComponent,
      data: { embedded: true },
    },
    {
      path: 'embed-legal',
      component: ReportsComponent,
      data: { embedded: true, embeddedReportTab: 'legal' },
    },
    {
      path: 'embed-operations',
      component: ReportsComponent,
      data: { embedded: true, embeddedReportTab: 'daily-repayments' },
    },
    {
      path: 'embed-merchant',
      component: ReportsComponent,
      data: { embedded: true, embeddedReportTab: 'all-sales' },
    },
    // Keep existing Salesforce Canvas configurations working while they move
    // to the canonical merchant-report URL.
    {
      path: 'embed-all',
      component: ReportsComponent,
      data: { embedded: true, embeddedReportTab: 'all-sales' },
    },
    {
      path: '**',
      redirectTo: 'uploaded-files',
    },
  ]
}

// 🔐 MSAL setup
function msalInstanceFactory(): IPublicClientApplication {
  const runtimeRedirectUri =
    typeof window !== 'undefined'
      ? `${window.location.origin}/uploaded-files`
      : APP_CONFIG.redirectUri

  return new PublicClientApplication({
    auth: {
      clientId: '5c6a4f59-a7c4-4bd3-83fa-03b44d81eeaa',
      authority:
        'https://login.microsoftonline.com/f90a991c-1b85-4127-8c23-dc88f7314cfb',
      redirectUri: runtimeRedirectUri,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: false,
    },
  })
}

function msalGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read'],
    },
  }
}

function msalInterceptorConfigFactory(): MsalInterceptorConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap: new Map([
      ['https://graph.microsoft.com/v1.0/me', ['user.read']],
    ]),
  }
}

function joinUrl(baseUrl: string, path: string): string {
  const trimmedBase = baseUrl.replace(/\/+$/, '')
  const trimmedPath = path.startsWith('/') ? path : `/${path}`
  return `${trimmedBase}${trimmedPath}`
}

async function postCanvasAuth<TPayload>(
  path: string,
  payload: TPayload,
  devKey?: string,
): Promise<Response> {
  const authUrl = joinUrl(APP_CONFIG.canvasAuthBaseUrl, path)
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (devKey) {
    headers['x-dev-exchange-key'] = devKey
  }

  if (isLocal) {
    return fetch(authUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    })
  }

  return fetch(authUrl, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify(payload),
  })
}

async function fetchDevPostgrestJwt(
  userEmail: string,
  devKey: string,
): Promise<string> {
  const resp = await fetch(APP_CONFIG.lambdaAuthUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-dev-exchange-key': devKey,
    },
    body: JSON.stringify({
      lambdaName: APP_CONFIG.swaggerPostgrestJwtLambdaName,
      payload: {},
      userEmail,
    }),
  })

  if (!resp.ok) {
    throw new Error(`Local auth JWT fetch failed: ${await resp.text()}`)
  }

  const token = await resp.json()
  if (typeof token !== 'string' || token.split('.').length !== 3) {
    throw new Error('Local auth returned an invalid JWT')
  }
  return token
}

function normalizeDevKey(raw: string): string {
  // URLSearchParams decodes '+' as space; dev keys are base64-like so restore '+'.
  return raw.replace(/ /g, '+')
}

function isJwtLike(token: string | null | undefined): token is string {
  return typeof token === 'string' && token.split('.').length === 3
}

function maybePersistDevKey(url: URL, storageKey: string): string | null {
  const rawDevKey = url.searchParams.get('devKey')
  const devKey = rawDevKey ? normalizeDevKey(rawDevKey) : null
  if (!devKey) {
    return null
  }
  localStorage.setItem(storageKey, devKey)
  url.searchParams.delete('devKey')
  history.replaceState({}, '', url.toString())
  return devKey
}

async function exchangeCanvasCode(
  authService: AuthService,
  code: string,
): Promise<void> {
  const resp = await postCanvasAuth('/auth/exchange', { code })
  if (!resp.ok) {
    throw new Error(`Canvas auth exchange failed: ${await resp.text()}`)
  }
  const payload = await resp.json()
  if (payload?.token) {
    authService.setToken(payload.token)
  }
  if (payload?.user) {
    authService.setEmbeddedUser(payload.user)
    if (isEmbedded) {
      console.log('[embed][auth] exchange payload', payload)
      console.log('[embed][auth] embedded user context', payload.user)
    }
  }
  if (!payload?.user) {
    throw new Error('Canvas auth exchange did not return a user')
  }
}

function stripAuthParamsFromUrl(): void {
  const url = new URL(location.href)
  let changed = false
  if (url.searchParams.has('canvasCode')) {
    url.searchParams.delete('canvasCode')
    changed = true
  }
  if (url.searchParams.has('devKey')) {
    url.searchParams.delete('devKey')
    changed = true
  }
  if (changed) {
    const next = `${url.pathname}${url.search}${url.hash}`
    history.replaceState({}, '', next)
  }
}

function authInitializerFactory(authService: AuthService) {
  return async () => {
    if (isLocal) {
      if (!isEmbedded) {
        return
      }
      const url = new URL(location.href)
      const storageKey = 'repaymentsDevExchangeKey'
      const devKeyFromQuery = maybePersistDevKey(url, storageKey)
      const devKey =
        devKeyFromQuery ||
        localStorage.getItem(storageKey) ||
        sessionStorage.getItem(storageKey)

      let devPayload: any = null
      if (isEmbedded) {
        const devResp = await postCanvasAuth(
          '/auth/dev-exchange',
          { mintCode: true },
          devKey || undefined,
        )
        if (!devResp.ok) {
          throw new Error(
            `Local auth dev-exchange failed: ${await devResp.text()}`,
          )
        }
        devPayload = await devResp.json()
        if (devPayload?.token) {
          authService.setToken(devPayload.token)
        }
        if (devPayload?.user) {
          authService.setEmbeddedUser(devPayload.user)
        }
        if (devPayload?.code) {
          await exchangeCanvasCode(authService, devPayload.code)
        }
      }
      if (!authService.postgrestJwtToken) {
        const devUserEmail =
          (devPayload?.user?.sf?.username as string | undefined) ??
          (devPayload?.user?.email as string | undefined) ??
          (devPayload?.user?.userEmail as string | undefined) ??
          (devPayload?.user?.username as string | undefined)
        if (devKey && devUserEmail) {
          const jwtToken = await fetchDevPostgrestJwt(devUserEmail, devKey)
          authService.setToken(jwtToken)
        }
      }
      if (!authService.postgrestJwtToken) {
        const storedToken = localStorage.getItem('repaymentsPostgrestJwt')
        if (isJwtLike(storedToken)) {
          authService.setToken(storedToken)
        } else {
          throw new Error(
            'Local auth did not provide a PostgREST token. Provide a dev exchange token or set localStorage key "repaymentsPostgrestJwt".',
          )
        }
      }
      stripAuthParamsFromUrl()
      return
    }

    if (!isEmbedded) {
      return
    }

    const code = new URLSearchParams(location.search).get('canvasCode')
    authService.enableEmbeddedMode()
    if (!code) {
      throw new Error('Missing Salesforce Canvas code for embedded view')
    }
    await exchangeCanvasCode(authService, code)
    stripAuthParamsFromUrl()
  }
}

function buildProviders(
  msalInstance: IPublicClientApplication,
): Array<Provider | EnvironmentProviders> {
  const providers: Array<Provider | EnvironmentProviders> = [
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    provideRouter(buildRoutes()),
    {
      provide: RouteReuseStrategy,
      useClass: TabRouteReuseStrategy,
    },
    provideAnimationsAsync(),
    {
      provide: MSAL_INSTANCE,
      useValue: msalInstance,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: authInitializerFactory,
      deps: [AuthService],
      multi: true,
    },
    providePrimeNG({
      theme: {
        preset: definePreset(Aura, FINANCE365_THEME_EXTENSION),
        options: { darkModeSelector: '.p-dark' },
      },
    }),
  ]

  if (useMsal) {
    providers.splice(1, 0, {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    } as Provider)
    providers.push(
      MsalGuard,
      MsalService,
      MsalBroadcastService,
      {
        provide: MSAL_GUARD_CONFIG,
        useFactory: msalGuardConfigFactory,
      } as Provider,
      {
        provide: MSAL_INTERCEPTOR_CONFIG,
        useFactory: msalInterceptorConfigFactory,
      } as Provider,
    )
  } else {
    providers.push(MsalService)
  }

  return providers
}

async function main() {
  const msalInstance = msalInstanceFactory()
  if (useMsal) {
    await msalInstance.initialize()
    await msalInstance.handleRedirectPromise().then((result) => {
      if (result?.account) {
        msalInstance.setActiveAccount(result.account)
      } else if (!msalInstance.getActiveAccount()) {
        const accounts = msalInstance.getAllAccounts()
        if (accounts.length > 0) {
          msalInstance.setActiveAccount(accounts[0])
        }
      }
      console.log('[MSAL] Redirect handled:', result)
    })
  }

  const appConfig: ApplicationConfig = {
    providers: buildProviders(msalInstance),
  }

  await bootstrapApplication(WrapperComponent, appConfig)
}

main().catch((err) => console.error(err))
